import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	type ReactNode,
} from 'react';

import type { IssueEventsApi, IssueEventNames, IssueEventPayloads } from './types.tsx';

class IssuePubSub<E extends string, P extends Record<E, unknown>> {
	private subscribers: Partial<{ [K in E]: Array<(payload: P[K]) => void> }> = {};

	subscribe<K extends E>(event: K, subscriber: (payload: P[K]) => void) {
		const list = this.subscribers[event];
		if (list) {
			list.push(subscriber);
		} else {
			this.subscribers[event] = [subscriber];
		}
		return () => this.unsubscribe(event, subscriber);
	}

	unsubscribe<K extends E>(event: K, subscriber: (payload: P[K]) => void) {
		const list = this.subscribers[event];
		if (!list) return;
		this.subscribers[event] = list.filter((s) => s !== subscriber);
	}

	publish<K extends E>(event: K, payload: P[K]) {
		this.subscribers[event]?.forEach((s) => s(payload));
	}
}

const IssuePubSubContext = createContext<IssueEventsApi | null>(null);

export function IssuePubSubProvider({ children }: { value?: IssueEventsApi; children: ReactNode }) {
	const issuePubSub = useContext(IssuePubSubContext);

	const newPubSub = useMemo(() => {
		if (issuePubSub === null) {
			return new IssuePubSub<IssueEventNames, IssueEventPayloads>(); // create a new pubsub if it's null
		}
		return issuePubSub;
	}, [issuePubSub]);

	return <IssuePubSubContext.Provider value={newPubSub}>{children}</IssuePubSubContext.Provider>;
}

export const useIssuePubSub = (): IssueEventsApi => {
	const issuePubSub = useContext(IssuePubSubContext);
	if (issuePubSub === null) {
		throw new Error('IssuePubSub is been used without a context.');
	}
	return issuePubSub;
};

export function usePublishIssueEvent() {
	const pubsub = useIssuePubSub();
	return useCallback(
		<K extends IssueEventNames>(event: K, payload: IssueEventPayloads[K]) => {
			pubsub.publish(event, payload);
		},
		[pubsub],
	);
}

export function useSubscribeToIssueEvent<K extends IssueEventNames>(
	event: K | K[],
	subscriber: (payload: IssueEventPayloads[K]) => void,
) {
	const pubsub = useIssuePubSub();
	useEffect(() => {
		const eventsArray: ReadonlyArray<K> = Array.isArray(event) ? event : [event];
		const unsubs = eventsArray.map((e) => pubsub.subscribe(e, subscriber));
		return () => {
			return unsubs.forEach((u) => u());
		};
	}, [pubsub, event, subscriber]);
}
